// src/mechanism/appBootstrap.js
import router from '@/router';
import { CookieEngine } from '@/mechanism/cookie-engine';
import {
    triggerAppLaunchHooks,
    triggerAppCloseHooks,
    triggerInForegroundHooks,
    triggerInBackgroundHooks,
    triggerNetworkOnlineHooks,
    triggerNetworkOfflineHooks
} from '@/mechanism/appHooks';
import { Storage } from "@/mechanism/storage";

let appStateListener = null;
let networkListener = null;
let lastWidth = window.innerWidth;
let isBooted = false;
let safeAreaObserver = null;
let resizeHandler = null;

const PRIVACY_KEY = 'has_agreed_privacy';

let isFirstForegroundEventIgnored = false;


/**
 * 平台环境辅助检测（零依赖，避免过早触碰 Capacitor 导致隐私合规问题）
 */
const isAndroidEnv = () => /android/i.test(navigator.userAgent);
const isPureWebEnv = () => !window.Capacitor && !navigator.userAgent.includes('Capacitor');

/**
 * 🌟 修复点：包装为异步函数，读取 Storage 里的授权状态
 */
const isAgreedLocally = async () => {
    try {
        const agreed = await Storage.get(PRIVACY_KEY);
        return !!agreed;
    } catch (e) {
        console.warn('读取隐私政策状态失败:', e);
        return false;
    }
};

/**
 * 🌟 修复点：定义独立函数避免 this 指向丢失，保证 removeEventListener 正常生效
 */
const onVisibilityChange = async () => {
    if (document.hidden) {
        triggerInBackgroundHooks();
    } else {
        const hasAgreed = (await isAgreedLocally()) || isAndroidEnv();
        if (hasAgreed) {
            triggerInForegroundHooks();
        }
    }
};

export const AppBootstrap = {
    /**
     * 1. 安全区域调试与注入源监控
     */
    initSafeAreaTracker() {
        // 监听 <html> style 属性注入
        safeAreaObserver = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    const rootStyles = getComputedStyle(document.documentElement);
                    const safeTop = rootStyles.getPropertyValue('--safe-area-inset-top').trim();

                    if (safeTop) {
                        console.log('🚨 抓到了！有人注入了 --safe-area-inset-top:', safeTop);
                        console.trace('查看注入来源');
                        safeAreaObserver?.disconnect();
                        safeAreaObserver = null;
                    }
                }
            }
        });

        safeAreaObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['style']
        });

        // 幽灵节点测量系统安全区
        setTimeout(() => {
            const rootStyles = getComputedStyle(document.documentElement);
            const customVar = rootStyles.getPropertyValue('--safe-area-inset-top').trim();

            const ghostDiv = document.createElement('div');
            ghostDiv.style.paddingTop = 'env(safe-area-inset-top, 0px)';
            ghostDiv.style.position = 'absolute';
            ghostDiv.style.visibility = 'hidden';
            document.body.appendChild(ghostDiv);

            const nativeEnv = getComputedStyle(ghostDiv).paddingTop;
            document.body.removeChild(ghostDiv);

            console.log('=============================');
            console.log('【前端框架变量】 --safe-area-inset-top:', customVar || '未设置(空)');
            console.log('【手机原生系统】 env(safe-area-inset-top):', nativeEnv);
            console.log('=============================');
        }, 100);
    },

    /**
     * 2. 纯前端视口锁定与防软键盘挤压高度
     */
    initPureWebViewport() {
        const lockAppViewport = (isInit = false) => {
            const currentWidth = window.innerWidth;
            if (isInit || currentWidth !== lastWidth) {
                const initHeight = window.innerHeight;
                document.documentElement.style.setProperty('--app-locked-height', `${initHeight}px`);
                lastWidth = currentWidth;
            } else {
                console.log('检测到宽度未变，判定为软键盘行为，忽略高度重新计算');
            }
        };

        setTimeout(() => lockAppViewport(true), 100);

        resizeHandler = () => {
            setTimeout(() => lockAppViewport(false), 200);
        };
        window.addEventListener('resize', resizeHandler);
    },

    /**
     * 3. 核心：授权完成后动态加载底座与服务
     */
    async bootCoreServices() {
        if (isBooted) return;
        isBooted = true;

        console.log('🛡️【合规校验通过】用户已授权，开始动态载入原生层及核心服务...');

        // 动态载入 Capacitor 核心模块
        const { Capacitor } = await import('@capacitor/core');
        const { App: CapApp } = await import('@capacitor/app');
        const { Keyboard } = await import('@capacitor/keyboard');
        const { Network } = await import('@capacitor/network');

        // 动态载入依赖原生环境的业务管理器
        const { PushManager } = await import('@/mechanism/PushManager');
        const { UpdateManager } = await import('@/mechanism/UpdateManager');
        const { IntentEngine } = await import('@/mechanism/IntentEngine');
        const { registerIntentEndpoints } = await import('@/mechanism/intentEndpoints/intentLogin');

        Keyboard.setAccessoryBarVisible({isVisible: false})
            .then(() => {
                console.log('Accessory Bar is successfully hidden');
            })
            .catch((err) => {
                console.error('Error hiding accessory bar', err);
            });

        // --- A. 意图引擎初始化 ---
        registerIntentEndpoints();
        await IntentEngine.init(router.currentRoute.value);
        IntentEngine.setAppReady();

        // --- B. 原生端配置与前后台生命周期监听 ---
        if (Capacitor.isNativePlatform()) {
            appStateListener = await CapApp.addListener('appStateChange', async ({ isActive }) => {
                if (isActive) {
                    // ✨ 过滤掉 App 刚冷启动成功时伴随触发的首次切前台广播，防止重复拉取
                    if (!isFirstForegroundEventIgnored) {
                        isFirstForegroundEventIgnored = true;
                        return;
                    }
                    const hasAgreed = (await isAgreedLocally()) || Capacitor.getPlatform() === 'android';
                    if (hasAgreed) {
                        triggerInForegroundHooks();
                    }
                } else {
                    triggerInBackgroundHooks();
                }
            });
        }

        // --- C. 网络状态监听 ---
        networkListener = await Network.addListener('networkStatusChange', (status) => {
            if (status.connected) {
                triggerNetworkOnlineHooks();
            } else {
                triggerNetworkOfflineHooks();
            }
        });

        // --- D. 检查热更新与原生 Cookie 同步 ---
        try {
            UpdateManager.init();
            const updateInfo = await UpdateManager.checkUpdate();

            if (updateInfo && updateInfo.type === 'hard') {
                router.replace({
                    name: 'ForceUpdate',
                    query: {
                        appId: updateInfo.appId,
                        notes: updateInfo.notes,
                        force: String(updateInfo.force),
                        version: updateInfo.version
                    }
                });
                return;
            }
            await CookieEngine.syncToAllNativeDomains();
        } catch (error) {
            console.warn('App 初始化网络请求异常，可能是首次安装未授权网络:', error);
        }

        // --- E. 启动推送与业务启动钩子 ---
        PushManager.init();
        triggerAppLaunchHooks();
    },

    /**
     * 启动入口
     */
    async init() {
        this.initSafeAreaTracker();
        this.initPureWebViewport();

        const isPureWeb = isPureWebEnv();
        const isAndroid = isAndroidEnv();
        // 🌟 修复点：通过 await 读取异步结果
        const hasAgreed = await isAgreedLocally();

        // Android 原生放行 或 纯 Web 环境 或 本地已同意
        if (hasAgreed || isAndroid || isPureWeb) {
            await this.bootCoreServices();
        } else {
            console.log('🛑【物理拦截】未获隐私许可，Capacitor 模块尚未被载入或执行');

            window.addEventListener('app:privacy-agreed', async () => {
                await this.bootCoreServices();
            }, { once: true });
        }
    },

    /**
     * 销毁及内存回收
     */
    async destroy() {
        triggerAppCloseHooks();

        // 1. 移除 DOM 观察者与视口监听
        if (safeAreaObserver) {
            safeAreaObserver.disconnect();
            safeAreaObserver = null;
        }
        if (resizeHandler) {
            window.removeEventListener('resize', resizeHandler);
            resizeHandler = null;
        }

        // 2. 移除前后台监听
        if (appStateListener) {
            await appStateListener.remove();
            appStateListener = null;
        }
        document.removeEventListener('visibilitychange', onVisibilityChange);

        // 3. 移除网络监听
        if (networkListener) {
            await networkListener.remove();
            networkListener = null;
        }
    }
};